import { Box, Grid, Skeleton, Button, Flex, Heading, Text, Icon } from '@chakra-ui/react';
import { RepeatIcon, WarningTwoIcon } from '@chakra-ui/icons';
import { useStatsForNerds } from '../hooks/useStatsForNerds';
import { StatsHeader } from '../components/statsForNerds/StatsHeader';
import { AlertsPanel } from '../components/statsForNerds/AlertsPanel';
import { SystemInfoBar } from '../components/statsForNerds/SystemInfoBar';
import { CpuCard } from '../components/statsForNerds/CpuCard';
import { MemoryCard } from '../components/statsForNerds/MemoryCard';
import { DisksCard } from '../components/statsForNerds/DisksCard';
import { NetworkCard } from '../components/statsForNerds/NetworkCard';
import { GpuCard } from '../components/statsForNerds/GpuCard';
import { ProcessesTable } from '../components/statsForNerds/ProcessesTable';

export function ServerStatsScreen() {
  const { data, loading, error, refresh, paused, setPaused, history } = useStatsForNerds({
    refreshMs: 5000,
    paused: false
  });

  const togglePause = () => {
    setPaused(!paused);
  };

  const renderSkeletonCard = () => (
    <Box borderWidth="1px" borderRadius="xl" p={6}>
      <Skeleton height="20px" width="33%" mb={4} />
      <Skeleton height="32px" width="100%" mb={2} />
      <Flex gap={2}>
        <Skeleton height="20px" width="25%" />
        <Skeleton height="20px" width="25%" />
      </Flex>
    </Box>
  );

  const renderErrorCard = () => (
    <Box borderWidth="1px" borderRadius="xl" p={6} borderColor="red.500">
      <Flex direction="column" alignItems="center" gap={4} textAlign="center">
        <Icon as={WarningTwoIcon} boxSize={8} color="red.500" />
        <Box>
          <Heading as="h3" size="md">Failed to load server stats</Heading>
          <Text fontSize="sm" color="gray.500" mt={1}>{error}</Text>
        </Box>
        <Button onClick={refresh} variant="outline" size="sm" leftIcon={<RepeatIcon />}>
          Retry
        </Button>
      </Flex>
    </Box>
  );

  return (
    <Box minH="100vh" p={{ base: 4, sm: 6, lg: 8 }}>
      <Box maxW="7xl" mx="auto" >
        {/* Header */}
        <StatsHeader
          data={data}
          loading={loading}
          paused={paused}
          onRefresh={refresh}
          onTogglePause={togglePause}
        />

        {/* Alerts Panel */}
        {data && (
          <AlertsPanel data={data} paused={paused} />
        )}

        {/* System Info */}
        {data && (
          <SystemInfoBar 
            osData={data.os}
            systemData={data.system}
            chassisData={data.chassis} 
            timestamp={data.ts} 
            paused={paused}
          />
        )}

        {/* Main Dashboard Grid */}
        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr", xl: "1fr 1fr" }} gap={6} mt={6}>
          {/* CPU Card */}
          {loading && !data ? (
            renderSkeletonCard()
          ) : error ? (
            renderErrorCard()
          ) : data ? (
            <CpuCard data={data.cpu} history={history} paused={paused} />
          ) : null}

          {/* Memory Card */}
          {loading && !data ? (
            renderSkeletonCard()
          ) : error ? (
            renderErrorCard()
          ) : data ? (
            <MemoryCard data={data.memory} paused={paused} />
          ) : null}

          {/* Disks Card */}
          {loading && !data ? (
            renderSkeletonCard()
          ) : error ? (
            renderErrorCard()
          ) : data ? (
            <DisksCard data={data.disks} paused={paused} />
          ) : null}

          {/* Network Card - Only show if network data exists */}
          {data?.network && data.network.length > 0 && (
            <NetworkCard data={data.network} history={history} paused={paused} />
          )}

          {/* GPU Card */}
          {loading && !data ? (
            renderSkeletonCard()
          ) : error ? (
            renderErrorCard()
          ) : data ? (
            <GpuCard data={data.gpu} paused={paused} />
          ) : null}
        </Grid>

        {/* Processes Table - Full Width */}
        <Box w="100%" mt={6}>
          {loading && !data ? (
            <Box borderWidth="1px" borderRadius="xl" p={6}>
              <Skeleton height="32px" width="100%" mb={4} />
              <Skeleton height="20px" width="100%" mb={2} />
              <Skeleton height="20px" width="75%" mb={2} />
              <Skeleton height="20px" width="50%" />
            </Box>
          ) : error ? (
            renderErrorCard()
          ) : data ? (
            <ProcessesTable data={data.topProcesses} paused={paused} />
          ) : null}
        </Box>

      </Box>
    </Box>
  );
}
